import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAssignSourceToDistrictManager } from "@/api/reseller/mutations";
import { useCreatedDistrictManagers } from "@/api/reseller/queries";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sn: string;
	currentManagerId: number | null;
};

export function AssignToDistrictManagerDialog({
	open,
	onOpenChange,
	sn,
	currentManagerId,
}: Props) {
	const { t } = useTranslation();
	const [managerId, setManagerId] = useState<string>("");

	useEffect(() => {
		if (open) setManagerId(currentManagerId ? String(currentManagerId) : "");
	}, [open, currentManagerId]);

	const assign = useAssignSourceToDistrictManager(sn);
	const { data } = useCreatedDistrictManagers({ page_size: 100 });
	const managers = data?.results ?? [];

	function handleSubmit() {
		if (!managerId) return;
		assign.mutate(
			{ district_manager_id: Number(managerId) },
			{
				onSuccess: () => {
					toast.success(t("mqttSources.assignToManagerSuccess"));
					onOpenChange(false);
				},
				onError: () => {
					toast.error(t("mqttSources.assignToManagerError"));
				},
			},
		);
	}

	function handleUnassign() {
		assign.mutate(
			{ district_manager_id: null },
			{
				onSuccess: () => {
					toast.success(t("mqttSources.unassignManagerSuccess"));
					onOpenChange(false);
				},
				onError: () => {
					toast.error(t("mqttSources.assignToManagerError"));
				},
			},
		);
	}

	const canSubmit =
		!!managerId && Number(managerId) !== currentManagerId && !assign.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("mqttSources.assignToManagerTitle")}</DialogTitle>
					<DialogDescription>
						{t("mqttSources.assignToManagerDescription", { sn })}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="manager-select">
							{t("mqttSources.colDistrictManager")}
						</Label>
						<Select value={managerId} onValueChange={setManagerId}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder={t("mqttSources.selectManager")} />
							</SelectTrigger>
							<SelectContent>
								{managers.map((m) => (
									<SelectItem key={m.id} value={String(m.id)}>
										{m.company_name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="ghost"
						onClick={() => onOpenChange(false)}
						disabled={assign.isPending}
					>
						{t("mqttSources.cancel")}
					</Button>
					{currentManagerId != null ? (
						<Button
							variant="outline"
							onClick={handleUnassign}
							disabled={assign.isPending}
						>
							{t("mqttSources.unassignManager")}
						</Button>
					) : null}
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{t("mqttSources.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
